/* ==========================================================================
   STF DIAMONDS — base.js
   Foundation behaviors, vanilla JS, no dependencies, no CDNs.

   Provides (via data attributes — no per-section JS needed for these):
   1. Overlay manager  — [data-open="#id"] opens any [data-overlay] element
                         (.drawer / .modal), [data-close] closes, Esc closes,
                         shared backdrop, focus trap, body scroll lock.
                         Events: "overlay:open" / "overlay:close" on the panel.
   2. Header hide      — [data-header-hide] hides on scroll down, returns on
                         scroll up; .is-scrolled past 10px.
   3. Carousel         — [data-carousel] with [data-carousel-track] and
                         [data-carousel-prev] / [data-carousel-next] buttons.
   4. Accordion groups — [data-accordion-group] keeps one <details> open.
   5. Lazy media       — fades imgs in, clears spinner (.lazy-media wrapper).
   6. Infinite sentinel— [data-infinite] dispatches "infinite:visible" when
                         scrolled into view (consumed by plp.js).
   7. Smooth scroll    — [data-scroll-to="#id"].
   8. Card swatches    — .product-card [data-swatch] swaps card image.

   Exposes window.STF = { openOverlay, closeOverlay }.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. OVERLAY MANAGER (drawers + modals)
     ------------------------------------------------------------------ */
  var overlayStack = [];
  var backdrop = null;
  var lastTrigger = null;

  function getBackdrop() {
    if (!backdrop) {
      backdrop = doc.createElement('div');
      backdrop.className = 'overlay-backdrop';
      backdrop.setAttribute('aria-hidden', 'true');
      backdrop.addEventListener('click', function () {
        closeTopOverlay();
      });
      doc.body.appendChild(backdrop);
    }
    return backdrop;
  }

  function focusables(el) {
    return Array.prototype.filter.call(
      el.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      function (node) {
        return node.offsetParent !== null || node === doc.activeElement;
      }
    );
  }

  function openOverlay(el, trigger) {
    if (!el || el.classList.contains('is-open')) return;
    lastTrigger = trigger || doc.activeElement;

    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    getBackdrop().classList.add('is-active');
    doc.body.classList.add('no-scroll');
    overlayStack.push(el);

    var focusEls = focusables(el);
    if (focusEls.length) focusEls[0].focus({ preventScroll: true });

    el.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
  }

  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');

    var idx = overlayStack.indexOf(el);
    if (idx > -1) overlayStack.splice(idx, 1);

    if (!overlayStack.length) {
      getBackdrop().classList.remove('is-active');
      doc.body.classList.remove('no-scroll');
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus({ preventScroll: true });
      }
      lastTrigger = null;
    }

    el.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
  }

  function closeTopOverlay() {
    if (overlayStack.length) closeOverlay(overlayStack[overlayStack.length - 1]);
  }

  doc.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-open]');
    if (opener) {
      var sel = opener.getAttribute('data-open');
      var panel = sel ? doc.querySelector(sel) : null;
      if (panel) {
        e.preventDefault();
        openOverlay(panel, opener);
      }
      return;
    }
    var closer = e.target.closest('[data-close]');
    if (closer) {
      e.preventDefault();
      var target = closer.closest('[data-overlay]');
      if (target) {
        closeOverlay(target);
      } else {
        closeTopOverlay();
      }
    }
  });

  doc.addEventListener('keydown', function (e) {
    if (!overlayStack.length) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeTopOverlay();
      return;
    }

    if (e.key === 'Tab') {
      var panel = overlayStack[overlayStack.length - 1];
      var els = focusables(panel);
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (e.shiftKey && doc.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Ensure every overlay starts hidden from AT
  doc.querySelectorAll('[data-overlay]').forEach(function (el) {
    el.setAttribute('aria-hidden', 'true');
  });

  window.STF = window.STF || {};
  window.STF.openOverlay = openOverlay;
  window.STF.closeOverlay = closeOverlay;

  /* ------------------------------------------------------------------
     2. HEADER HIDE-ON-SCROLL
     ------------------------------------------------------------------ */
  var header = doc.querySelector('[data-header-hide]');
  if (header) {
    var lastY = window.scrollY;
    var ticking = false;

    var onScroll = function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);

      if (y > lastY && y > 160 && !overlayStack.length) {
        header.classList.add('is-hidden');
      } else if (y < lastY) {
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(onScroll);
      }
    }, { passive: true });

    onScroll();
  }

  /* ------------------------------------------------------------------
     3. CAROUSELS — snap-scroll + prev/next
     ------------------------------------------------------------------ */
  function initCarousel(root) {
    var track = root.querySelector('[data-carousel-track]');
    var prev = root.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]');
    if (!track) return;

    function step() {
      var item = track.children[0];
      if (!item) return 300;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 16;
      return item.getBoundingClientRect().width + gap;
    }

    function updateButtons() {
      var maxScroll = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll;
    }

    function scrollByStep(dir) {
      track.scrollBy({
        left: dir * step(),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { scrollByStep(-1); });
    if (next) next.addEventListener('click', function () { scrollByStep(1); });

    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(updateButtons);
    }, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  }

  doc.querySelectorAll('[data-carousel]').forEach(initCarousel);

  // Sections rendered/re-rendered by the theme editor
  doc.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-carousel]').forEach(initCarousel);
    e.target.querySelectorAll('[data-overlay]').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
  });

  /* ------------------------------------------------------------------
     4. ACCORDION GROUPS — single-open <details>
     ------------------------------------------------------------------ */
  doc.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details.open) return;
    var group = details.closest('[data-accordion-group]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach(function (other) {
      if (other !== details) other.open = false;
    });
  }, true);

  /* ------------------------------------------------------------------
     5. LAZY MEDIA — fade in + clear spinner
     ------------------------------------------------------------------ */
  function markLoaded(img) {
    img.classList.add('is-loaded');
    var wrap = img.closest('.lazy-media');
    if (wrap) wrap.classList.add('is-loaded');
  }

  function watchImages(scope) {
    (scope || doc).querySelectorAll('.lazy-media img').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        img.addEventListener('load', function () { markLoaded(img); }, { once: true });
        img.addEventListener('error', function () { markLoaded(img); }, { once: true });
      }
    });
  }

  watchImages();
  window.STF.watchImages = watchImages;

  /* ------------------------------------------------------------------
     6. INFINITE-LOAD SENTINEL
     ------------------------------------------------------------------ */
  if ('IntersectionObserver' in window) {
    var sentinelObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.dispatchEvent(
            new CustomEvent('infinite:visible', { bubbles: true })
          );
        }
      });
    }, { rootMargin: '400px 0px' });

    var watchSentinels = function (scope) {
      (scope || doc).querySelectorAll('[data-infinite]').forEach(function (el) {
        sentinelObserver.observe(el);
      });
    };
    watchSentinels();
    window.STF.watchSentinels = watchSentinels;
  }

  /* ------------------------------------------------------------------
     7. SMOOTH SCROLL — [data-scroll-to="#id"]
     ------------------------------------------------------------------ */
  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-scroll-to]');
    if (!trigger) return;
    var target = doc.querySelector(trigger.getAttribute('data-scroll-to'));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  });

  /* ------------------------------------------------------------------
     8. PRODUCT CARD METAL SWATCHES — swap card image per metal variant
     ------------------------------------------------------------------ */
  doc.addEventListener('click', function (e) {
    var swatch = e.target.closest('.product-card [data-swatch]');
    if (!swatch) return;
    e.preventDefault();

    var card = swatch.closest('.product-card');
    var img = card ? card.querySelector('.product-card__media img') : null;
    var src = swatch.getAttribute('data-swatch-image');
    var srcset = swatch.getAttribute('data-swatch-srcset');

    card.querySelectorAll('[data-swatch]').forEach(function (s) {
      s.classList.toggle('is-active', s === swatch);
      s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
    });

    if (img && src && img.getAttribute('src') !== src) {
      img.classList.remove('is-loaded');
      var wrap = img.closest('.lazy-media');
      if (wrap) wrap.classList.remove('is-loaded');
      img.setAttribute('src', src);
      if (srcset) {
        img.setAttribute('srcset', srcset);
      } else {
        img.removeAttribute('srcset');
      }
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        img.addEventListener('load', function () { markLoaded(img); }, { once: true });
      }
    }
  });
})();

/* ==========================================================================
   MOTION SYSTEM
   9.  Scroll reveal — IntersectionObserver adds .is-revealed to any
       [data-reveal] element (variants: default rise / "fade" / "scale",
       styled in base.css). Stagger via data-reveal-delay="ms". Each
       element is unobserved after revealing; the observer disconnects
       once nothing is left. Fully disabled under prefers-reduced-motion:
       the html.stf-motion gate (set inline in theme.liquid) never hides
       content for those visitors, and this engine exits early.
   10. Banner parallax — [data-parallax] wrappers get a gentle rAF-driven
       translateY (max 40px) on their inner image. Desktop only, never
       under reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMq.matches) {
    document.documentElement.classList.remove('stf-motion');
    return;
  }

  /* ---- 9. SCROLL REVEAL ------------------------------------------------ */
  if (!('IntersectionObserver' in window)) {
    document.documentElement.classList.remove('stf-motion');
  } else {
    document.documentElement.classList.add('stf-motion');

    var pending = 0;
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
        if (delay > 0) el.style.transitionDelay = delay + 'ms';
        el.classList.add('is-revealed');
        revealObserver.unobserve(el);
        pending -= 1;
        if (pending <= 0) revealObserver.disconnect();
        // Clear the stagger delay once the reveal has played so later
        // transitions (hover states etc.) are not delayed.
        window.setTimeout(function () {
          el.style.transitionDelay = '';
        }, delay + 700);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    var watchReveals = function (scope) {
      (scope || document)
        .querySelectorAll('[data-reveal]:not(.is-revealed)')
        .forEach(function (el) {
          pending += 1;
          revealObserver.observe(el);
        });
    };

    watchReveals();

    // Theme editor: newly (re)rendered sections reveal immediately-on-view.
    document.addEventListener('shopify:section:load', function (e) {
      watchReveals(e.target);
    });

    window.STF = window.STF || {};
    window.STF.watchReveals = watchReveals;
  }

  /* ---- 10. BANNER PARALLAX --------------------------------------------- */
  var mobileMq = window.matchMedia('(max-width: 768px)');
  var MAX_SHIFT = 40;
  var items = [];

  function collectParallax() {
    items = [];
    document.querySelectorAll('[data-parallax]').forEach(function (el) {
      var img = el.querySelector('img');
      if (img) items.push({ el: el, img: img });
    });
  }

  var parallaxTicking = false;

  function updateParallax() {
    parallaxTicking = false;
    var disabled = mobileMq.matches || reduceMq.matches;
    var vh = window.innerHeight;

    items.forEach(function (item) {
      if (disabled) {
        if (item.img.style.transform) item.img.style.transform = '';
        return;
      }
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;

      // -1 (banner below viewport center) … 1 (above): image drifts
      // slower than the page for a quiet depth cue.
      var progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
      var max = Math.min(MAX_SHIFT, rect.height * 0.018);
      var shift = Math.max(-max, Math.min(max, -progress * max));
      item.img.style.transform =
        'translateY(' + shift.toFixed(1) + 'px) scale(1.05)';
    });
  }

  function requestParallax() {
    if (parallaxTicking || !items.length) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  }

  collectParallax();
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);
  if (mobileMq.addEventListener) {
    mobileMq.addEventListener('change', requestParallax);
  }
  requestParallax();

  document.addEventListener('shopify:section:load', function () {
    collectParallax();
    requestParallax();
  });
})();

/* card drag-to-wap: pointer drag (>24px horizontal) toggles wear shot; hover handled by CSS */
(function(){
  var startX=null, target=null;
  document.addEventListener('pointerdown', function(e){
    var link=e.target.closest('[data-card-swap].product-card__link--has-swap');
    if(!link) return;
    startX=e.clientX; target=link;
  }, {passive:true});
  document.addEventListener('pointermove', function(e){
    if(startX===null||!target) return;
    if(Math.abs(e.clientX-startX)>24){
      target.classList.toggle('is-swapped');
      startX=null; target=null;
    }
  }, {passive:true});
  document.addEventListener('pointerup', function(){ startX=null; target=null; }, {passive:true});
})();


/* 360-spin hover video (product cards) — 2026-07-10.
   preload=none; first hover lazy-loads, then plays; pause on leave.
   Skipped when the user prefers reduced motion. */
(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.addEventListener('mouseover', function (e) {
    var media = e.target.closest && e.target.closest('.product-card__media');
    if (!media) return;
    var vid = media.querySelector('[data-hover-video]');
    if (!vid || vid.dataset.spinFailed) return;
    if (!vid.dataset.spinLoading) {
      vid.dataset.spinLoading = '1';
      vid.addEventListener('loadeddata', function () { vid.dataset.spinReady = '1'; }, { once: true });
      vid.load();
    }
    var p = vid.play();
    if (p && p.catch) p.catch(function (err) {
      if (!err || err.name !== 'AbortError') vid.dataset.spinFailed = '1';
    });
  });
  document.addEventListener('mouseout', function (e) {
    var media = e.target.closest && e.target.closest('.product-card__media');
    if (!media || media.contains(e.relatedTarget)) return;
    var vid = media.querySelector('[data-hover-video]');
    if (vid && !vid.paused) vid.pause();
  });
})();


/* 360-spin in-view video (category tiles) — 2026-07-10.
   hero-card renders [data-spin-view] with preload=none; this observer
   lazy-loads and plays each loop only while the tile is in the viewport,
   pausing off-screen. Skipped when the user prefers reduced motion
   (CSS hides the video, leaving the poster image underneath). */
(function () {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  var vids = document.querySelectorAll('video[data-spin-view]');
  if (!vids.length) return;

  function startSpin(vid) {
    if (vid.dataset.spinFailed) return;
    if (!vid.dataset.spinLoading) {
      vid.dataset.spinLoading = '1';
      vid.addEventListener('loadeddata', function () { vid.dataset.spinReady = '1'; }, { once: true });
      vid.load();
    }
    var p = vid.play();
    if (p && p.catch) p.catch(function (err) {
      if (!err || err.name !== 'AbortError') vid.dataset.spinFailed = '1';
    });
  }

  if (!('IntersectionObserver' in window)) {
    vids.forEach(startSpin);
    return;
  }

  var spinObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        startSpin(entry.target);
      } else if (!entry.target.paused) {
        entry.target.pause();
      }
    });
  }, { rootMargin: '80px 0px' });

  vids.forEach(function (vid) { spinObserver.observe(vid); });
})();
